import { Component, OnInit, OnDestroy, ElementRef, QueryList, ViewChildren } from '@angular/core';
import { Subscription } from 'rxjs';
import { RegimentService } from '@services/regiment.service';
import { TransitionService } from '@services/transition.service';
import {
  Regiment,
  TrainingRecommendation,
  SubscoreFlag,
  AssessmentLevel,
  TestProfile,
  TrainingType
} from '@models/regiment.model';
import {
  trainingListAnimation,
  trainingItemExpandAnimation,
  headerFadeAnimation
} from '@common/animations/regiment-transitions';

/**
 * Regiment Component
 * 
 * A landing page component that displays the current week's training regiment.
 * Shows training types with icons, session circles, and highlighted focus areas.
 * Includes a test profile selector for development/testing purposes.
 * 
 * Supports shared element transitions to app details via TransitionService.
 */
@Component({
  selector: 'app-regiment',
  templateUrl: './regiment.component.html',
  styleUrls: ['./regiment.component.scss'],
  animations: [trainingListAnimation, trainingItemExpandAnimation, headerFadeAnimation]
})
export class RegimentComponent implements OnInit, OnDestroy {

  /** Reference to training item elements for animation */
  @ViewChildren('trainingItem') trainingItems: QueryList<ElementRef>;

  /** Current week's regiment */
  regiment: Regiment | null = null;

  /** Loading state */
  isLoading = true;

  /** Error state */
  hasError = false;
  errorMessage = '';

  /** Available test profiles */
  testProfiles: TestProfile[] = [];

  /** Currently selected test profile ID */
  selectedProfileId = 'real';

  /** Track which item is selected for animation */
  selectedTrainingType: string | null = null;

  /** Animation state for each training item */
  itemAnimationStates: Map<string, 'default' | 'collapsed' | 'selected'> = new Map();

  /** Whether a transition is in progress */
  isTransitioning = false;

  /** Subscriptions for cleanup */
  private subscriptions: Subscription[] = [];

  constructor(
    private regimentService: RegimentService,
    private transitionService: TransitionService
  ) {
    this.testProfiles = this.regimentService.TEST_PROFILES;
  }

  ngOnInit(): void {
    // Load regiment
    this.loadRegiment();

    // Subscribe to regiment changes
    const sub = this.regimentService.currentRegiment$.subscribe(regiment => {
      if (regiment) {
        this.regiment = regiment;
        this.isLoading = false;
      }
    });
    this.subscriptions.push(sub);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Load the current week's regiment
   */
  async loadRegiment(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;

    try {
      this.regiment = await this.regimentService.getCurrentWeekRegiment();
      this.isLoading = false;
    } catch (error) {
      console.error('Error loading regiment:', error);
      this.hasError = true;
      this.errorMessage = 'Unable to load your training regiment. Please try again later.';
      this.isLoading = false;
    }
  }

  /**
   * Handle test profile selection change
   */
  async onProfileChange(): Promise<void> {
    this.regimentService.setTestProfile(this.selectedProfileId);
    
    // Regenerate regiment with new profile
    this.isLoading = true;
    try {
      this.regiment = await this.regimentService.regenerateCurrentRegiment();
    } catch (error) {
      console.error('Error regenerating regiment:', error);
    }
    this.isLoading = false;
  }

  /**
   * Toggle a session circle completion state
   */
  async toggleSession(recommendation: TrainingRecommendation, sessionIndex: number): Promise<void> {
    await this.regimentService.toggleSessionComplete(
      recommendation.trainingType as TrainingType,
      sessionIndex
    );
  }

  /**
   * Get the assessment level description for display
   */
  getAssessmentLevelDescription(): string {
    if (!this.regiment) { return ''; }
    return this.regimentService.getAssessmentLevelDescription(this.regiment.overallAssessmentLevel);
  }

  /**
   * Get focus areas summary for display
   */
  getFocusAreasSummary(): string {
    if (!this.regiment) { return ''; }
    return this.regimentService.getFocusAreasSummary(this.regiment.subscoreFlags);
  }

  /**
   * Get CSS class for assessment level badge
   */
  getAssessmentLevelClass(): string {
    if (!this.regiment) { return ''; }
    
    switch (this.regiment.overallAssessmentLevel) {
      case AssessmentLevel.High:
        return 'level-high';
      case AssessmentLevel.Low:
        return 'level-low';
      case AssessmentLevel.Average:
      default:
        return 'level-average';
    }
  }

  /**
   * Get CSS class for training item based on adjustment
   */
  getTrainingClass(recommendation: TrainingRecommendation): string {
    if (recommendation.adjustment > 0) {
      return 'adjustment-increased';
    } else if (recommendation.adjustment < 0) {
      return 'adjustment-decreased';
    }
    return 'adjustment-baseline';
  }

  /**
   * Get adjustment label for training item
   */
  getAdjustmentLabel(recommendation: TrainingRecommendation): string {
    if (recommendation.adjustment > 0) {
      return `+${recommendation.adjustment}`;
    } else if (recommendation.adjustment < 0) {
      return `${recommendation.adjustment}`;
    }
    return '';
  }

  /**
   * Check if there are focus areas to display
   */
  hasFocusAreas(): boolean {
    return this.regiment?.subscoreFlags && this.regiment.subscoreFlags.length > 0;
  }

  /**
   * Get formatted week date range for display
   */
  getWeekDateRange(): string {
    if (!this.regiment?.weekStartDate) { return ''; }
    
    const startDate = new Date(this.regiment.weekStartDate);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 6);

    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    const startStr = startDate.toLocaleDateString('en-US', options);
    const endStr = endDate.toLocaleDateString('en-US', options);

    return `${startStr} - ${endStr}`;
  }

  /**
   * Get completion percentage for a training recommendation
   */
  getCompletionPercentage(recommendation: TrainingRecommendation): number {
    return this.regimentService.getCompletionPercentage(recommendation);
  }

  /**
   * Get the number of completed sessions
   */
  getCompletedCount(recommendation: TrainingRecommendation): number {
    if (!recommendation.sessionsCompleted) { return 0; }
    return recommendation.sessionsCompleted.filter(s => s).length;
  }

  /**
   * Get the description of the currently selected test profile
   */
  getSelectedProfileDescription(): string {
    const profile = this.testProfiles.find(p => p.id === this.selectedProfileId);
    return profile ? profile.description : '';
  }

  /**
   * Handle click on a training item - triggers shared element transition
   */
  onTrainingItemClick(
    event: Event,
    recommendation: TrainingRecommendation,
    itemElement: HTMLElement
  ): void {
    // Prevent if already transitioning
    if (this.isTransitioning) return;

    // Find the icon element within the item
    const iconElement = itemElement.querySelector('.training-icon') as HTMLElement;
    if (!iconElement) {
      console.warn('Could not find icon element for transition');
      return;
    }

    // Set animation states
    this.isTransitioning = true;
    this.selectedTrainingType = recommendation.trainingType;

    // Update animation states for all items
    this.regiment?.trainingRecommendations.forEach(rec => {
      if (rec.trainingType === recommendation.trainingType) {
        this.itemAnimationStates.set(rec.trainingType, 'selected');
      } else {
        this.itemAnimationStates.set(rec.trainingType, 'collapsed');
      }
    });

    // Start the shared element transition
    this.transitionService.startTransition(
      itemElement,
      iconElement,
      recommendation.trainingType,
      recommendation.displayName,
      recommendation.iconPath
    );
  }

  /**
   * Get animation state for a training item
   */
  getItemAnimationState(trainingType: string): string {
    return this.itemAnimationStates.get(trainingType) || 'default';
  }

  /**
   * Reset animation states (called when returning to this view)
   */
  resetAnimationStates(): void {
    this.isTransitioning = false;
    this.selectedTrainingType = null;
    this.itemAnimationStates.clear();
  }

  /**
   * Track by function for training items
   */
  trackByTrainingType(index: number, rec: TrainingRecommendation): string {
    return rec.trainingType;
  }
}
